/**
 * This file is part of the Goobi viewer - a content presentation and management
 * application for digitized objects.
 * 
 * Visit these websites for more information. - http://www.intranda.com -
 * http://digiverso.com
 * 
 * This program is free software; you can redistribute it and/or modify it under the terms
 * of the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A
 * PARTICULAR PURPOSE. See the GNU General Public License for more details.
 * 
 * You should have received a copy of the GNU General Public License along with this
 * program. If not, see <http://www.gnu.org/licenses/>.
 * 
 * Module which draws rectangles to the image to highlight areas.
 * 
 * @version 3.2.0
 * @module viewImage.drawRect
 * @requires jQuery
 */

import HitAreas from "../overlays/HitAreas.js";
import { Subject, fromEventPattern, fromEvent } from "rxjs";
import { filter } from "rxjs/operators";

const _debug = false;    
const _hbAdd = 5;
const _sideClickPrecision = 10;

const settings = {
    defaultCursor : "auto",
    allowDragging :true,
    startCondition: () => true,
}

class Transform {

    constructor(viewer, overlay, config) {
        this.viewer = viewer;
        this.config = jQuery.extend(true, {}, settings, config);
        this.transforming = false;
        this.overlay = overlay;
        this.drawArea = null;
        this.startPoint = null;
        this.delta = _sideClickPrecision;
        this.finishedObservable = new Subject();
        this.cursor = undefined;
        this.onOverlay = false;

        this.canvasLocation = undefined;
        
        this.subscriptions = [
            fromEventPattern( handler => this.viewer.openseadragon.addHandler("canvas-press", handler), handler => this.viewer.openseadragon.removeHandler("canvas-press", handler))
            .subscribe(e => this.handlePress(e)),
            fromEventPattern( handler => this.viewer.openseadragon.addHandler("canvas-drag", handler), handler => this.viewer.openseadragon.removeHandler("canvas-drag", handler))
            .subscribe(e => this.handleDrag(e)),
            fromEventPattern( handler => this.viewer.openseadragon.addHandler("canvas-release", handler), handler => this.viewer.openseadragon.removeHandler("canvas-release", handler))
            .subscribe(e => this.handleRelease(e)),
            fromEvent(this.viewer.element, "mousemove")
            .pipe(filter(e => this.config.startCondition(e)))
            .subscribe(e => this.handleMoveOnCanvas(e)),
            fromEvent(this.overlay.element, "mouseout")
            .subscribe(e => this.handleLeaveElement(e)),
        ]

    }

    handleLeaveElement(event) {
        this.viewer.element.style.cursor = settings.defaultCursor;
    }

    handleMoveOnCanvas(event) {
        const eventInCanvas = new OpenSeadragon.Point(event.clientX - this.viewer.element.getBoundingClientRect().left, event.clientY - this.viewer.element.getBoundingClientRect().top);
        const canvasRect = this.overlay.getBoundsInCanvas();
        this.hitArea = this.calculateHitArea(eventInCanvas,canvasRect);
        if(this.hitArea && (this.config.allowDragging || this.hitArea !== HitAreas.CENTER)) {
            this.viewer.element.style.cursor = HitAreas.getCursor(this.hitArea);
        } else {
            this.hitArea = undefined;
            this.viewer.element.style.cursor = this.config.defaultCursor;
        }
    }



    handlePress(event) {
        if(this.hitArea && this.config.startCondition(event)) {
            this.transforming = true;
            this.viewer.disablePan();
            this.canvasLocation = new OpenSeadragon.Point(event.position.x, event.position.y);
            this.startPoint = this.canvasLocation;
        }
    }

    handleDrag(event) {
        if(this.transforming) {
            this.canvasLocation = new OpenSeadragon.Point(event.position.x, event.position.y);
            const distanceFromStart = this.canvasLocation.minus(this.startPoint);
            this.startPoint = this.canvasLocation;
            const boundsInCanvas = this.overlay.getBoundsInCanvas();
            const transformedRect = this.calculateTransformedRect2(boundsInCanvas, distanceFromStart, this.hitArea);

            const transformedRectViewport = this.overlay.convertFromCanvasToViewport(transformedRect);
            this.overlay.setBounds(transformedRectViewport);
        }
    }
    
    handleRelease(event) {
             if ( this.transforming) {
                this.finishedObservable.next(this.overlay);
            }
            this.transforming = false;
            this.viewer.enablePan();
    }

    finishedTransforming() {
        return this.finishedObservable;
    }

    close() {
        this.finishedObservable.unsubscribe();
        this.subscriptions.forEach(subscr => subscr.unsubscribe());
        this.subscriptions = [];
    }
    
    isTransforming() {
        return this.transforming;
    }

    calculateHitArea(point, rect) {

        if(!rect.containsPoint(point, this.delta)) {
            return undefined;
        }

        const distTopLeft = rect.getTopLeft().minus(point);
        if(Math.abs(distTopLeft.x) < this.delta && Math.abs(distTopLeft.y) < this.delta) {
            return HitAreas.TOPLEFT;
        }
        const distTopRight = rect.getTopRight().minus(point);
        if(Math.abs(distTopRight.x) < this.delta && Math.abs(distTopRight.y) < this.delta) {
            return HitAreas.TOPRIGHT;
        }
        const distBottomLeft = rect.getBottomLeft().minus(point);
        if(Math.abs(distBottomLeft.x) < this.delta && Math.abs(distBottomLeft.y) < this.delta) {
            return HitAreas.BOTTOMLEFT;
        }
        const distBottomRight = rect.getBottomRight().minus(point);
        if(Math.abs(distBottomRight.x) < this.delta && Math.abs(distBottomRight.y) < this.delta) {
            return HitAreas.BOTTOMRIGHT;
        }

        if(Math.abs(rect.y - point.y) < this.delta) {
            return HitAreas.TOP;
        }
        if(Math.abs((rect.y+rect.height) - point.y) < this.delta) {
            return HitAreas.BOTTOM;
        }
        if(Math.abs(rect.x - point.x) < this.delta) {
            return HitAreas.LEFT;
        }
        const distLeft = rect.x - point.x;
        if(Math.abs((rect.x+rect.width) - point.x) < this.delta) {
            return HitAreas.RIGHT;
        }

        return HitAreas.CENTER;

    }

    calculateTransformedRect2(rect, distance, hitArea) {
            
        //console.log("transform ", rect, distance, hitArea);

            var topLeft = null;//rect.getTopLeft();
            var bottomRight = null;//rect.getBottomRight();

            const dx = distance.x;
            const dy = distance.y;

            if ( hitArea === HitAreas.TOPLEFT ) {

                topLeft = new OpenSeadragon.Point( Math.min( rect.getTopLeft().x + dx, rect.getBottomRight().x ), Math.min( rect.getTopLeft().y + dy, rect.getBottomRight().y ) );
                bottomRight = rect.getBottomRight();
            }
            else if ( hitArea === HitAreas.TOPRIGHT ) {
                topLeft = new OpenSeadragon.Point( rect.getTopLeft().x, Math.min( rect.getTopLeft().y + dy, rect.getBottomRight().y ) );
                bottomRight = new OpenSeadragon.Point( Math.max( rect.getBottomRight().x + dx, rect.getTopLeft().x ), rect.getBottomRight().y );
            }
            else if ( hitArea === HitAreas.BOTTOMLEFT ) {
                topLeft = new OpenSeadragon.Point( Math.min( rect.getTopLeft().x + dx, rect.getBottomRight().x ), rect.getTopLeft().y );
                bottomRight = new OpenSeadragon.Point( rect.getBottomRight().x, Math.max( rect.getBottomRight().y + dy, rect.getTopLeft().y ) );
            }
            else if ( hitArea === HitAreas.BOTTOMRIGHT ) {
                topLeft = rect.getTopLeft();
                bottomRight = new OpenSeadragon.Point( Math.max(  rect.getBottomRight().x + dx, rect.getTopLeft().x ), Math.max(  rect.getBottomRight().y + dy, rect.getTopLeft().y ) );
            }
            else if ( hitArea === HitAreas.LEFT ) {
                topLeft = new OpenSeadragon.Point( Math.min( rect.getTopLeft().x + dx, rect.getBottomRight().x ), rect.getTopLeft().y );
                bottomRight = rect.getBottomRight();
            }
            else if ( hitArea === HitAreas.RIGHT ) {
                topLeft = rect.getTopLeft();
                bottomRight = new OpenSeadragon.Point( Math.max( rect.getBottomRight().x + dx, rect.getTopLeft().x ), rect.getBottomRight().y );
            }
            else if ( hitArea === HitAreas.TOP ) {
                topLeft = new OpenSeadragon.Point( rect.getTopLeft().x, Math.min( rect.getTopLeft().y + dy, rect.getBottomRight().y ) );
                bottomRight = rect.getBottomRight();
            }
            else if ( hitArea === HitAreas.BOTTOM ) {
                topLeft = rect.getTopLeft();
                bottomRight = new OpenSeadragon.Point( rect.getBottomRight().x, Math.max( rect.getBottomRight().y + dy, rect.getTopLeft().y ) );
            }
            else if ( hitArea === HitAreas.CENTER ) {
                return rect.translate(distance);
            }
            
            if(topLeft && bottomRight) {
                rect = new OpenSeadragon.Rect(topLeft.x, topLeft.y, bottomRight.x-topLeft.x, bottomRight.y-topLeft.y);
                return rect;
            }
    }

    calculateTransformedRect(rect, distance, hitArea) {
            
        //console.log("transform ", rect, distance, hitArea);

            var topLeft = null;//rect.getTopLeft();
            var bottomRight = null;//rect.getBottomRight();

            const dx = distance.x;
            const dy = distance.y;

            const x1 = rect.x;
            const y1 = rect.y;
            const x2 = rect.width + rect.x;
            const y2 = rect.height + rect.y;

            if(dx === dy === 0) {
                return rect;
            }

            if ( hitArea === HitAreas.TOPLEFT ) {

                topLeft = new OpenSeadragon.Point( Math.min( x1 + dx, x2 ), Math.min( y1 + dy, y2 ) );
                bottomRight = new OpenSeadragon.Point( Math.max( x1, x2 ), Math.max( y1, y2 ) );
            }
            else if ( hitArea === HitAreas.TOPRIGHT ) {
                topLeft = new OpenSeadragon.Point( x1, Math.min( y1 + dy, y2 ) );
                bottomRight = new OpenSeadragon.Point( Math.max( x2 + dx, x1 ), y2 );
            }
            else if ( hitArea === HitAreas.BOTTOMLEFT ) {
                topLeft = new OpenSeadragon.Point( Math.min( x1 + dx, x2 ), y1 );
                bottomRight = new OpenSeadragon.Point( x2, Math.max( y2 + dy, y1 ) );
            }
            else if ( hitArea === HitAreas.BOTTOMRIGHT ) {
                topLeft = new OpenSeadragon.Point( Math.min( x1, x2 ), Math.min( y1, y2 ) );
                bottomRight = new OpenSeadragon.Point( Math.max(  x2 + dx, x1 ), Math.max(  y2 + dy, y1 ) );
            }
            else if ( hitArea === HitAreas.LEFT ) {
                topLeft = new OpenSeadragon.Point( Math.min( x1 + dx, x2 ), y1 );
                bottomRight = new OpenSeadragon.Point( Math.max( x1, x2 ), Math.max( y1, y2 ) );
            }
            else if ( hitArea === HitAreas.RIGHT ) {
                topLeft = new OpenSeadragon.Point( Math.min( x1, x2 ), Math.min( y1, y2 ) );
                bottomRight = new OpenSeadragon.Point( Math.max( x2 + dx, x1 ), y2 );
            }
            else if ( hitArea === HitAreas.TOP ) {
                topLeft = new OpenSeadragon.Point( x1, Math.min( y1 + dy, y2 ) );
                bottomRight = new OpenSeadragon.Point( Math.max( x1, x2 ), Math.max( y1, y2 ) );
            }
            else if ( hitArea === HitAreas.BOTTOM ) {
                topLeft = new OpenSeadragon.Point( Math.min( x1, x2 ), Math.min( y1, y2 ) );
                bottomRight = new OpenSeadragon.Point( x2, Math.max( y2 + dy, y1) );
            }
            else if ( hitArea === HitAreas.CENTER ) {
                return rect.translate(distance);
            }
            
            if(topLeft && bottomRight) {
                let transformed = new OpenSeadragon.Rect(topLeft.x, topLeft.y, bottomRight.x-topLeft.x, bottomRight.y-topLeft.y);
                //console.log("transformed rect ", transformed);
                return transformed;
            }
    }


}

export default Transform;